import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";

import { DataTable, EmptyState, Loading, Refusal } from "@lopie/kernel-ui";

import { recordColumns } from "../lib/drill.js";
import { formatCell, humanise } from "../lib/spec.js";
import { isBadQuestion } from "../lib/run.js";

const show = (field, value, fields) => {
  if (value === undefined) return "";
  const col = field?.minor_units ? { type: "money", currency_field: field.currency_field } : {};
  if (value !== null && typeof value === "object") return JSON.stringify(value);
  return formatCell(col, value, fields || {});
};

/** Every field of one record, as the kernel returned it for this person. */
function RecordDetail({ record, schema, onClose }) {
  const type = (schema?.types || []).find((t) => t.type === record?.type);
  const fields = record?.fields || {};
  const named = new Map((type?.fields || []).map((f) => [f.name, f]));
  return (
    <Dialog open={Boolean(record)} onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="record-title">
      <DialogTitle id="record-title">{record?.type} record</DialogTitle>
      <DialogContent dividers>
        <Stack component="dl" sx={{ gap: 1, m: 0 }}>
          {Object.entries(fields).map(([name, value]) => (
            <Box key={name}>
              <Typography component="dt" variant="caption" color="text.secondary">{humanise(name)}</Typography>
              <Typography component="dd" variant="body2" sx={{ m: 0, overflowWrap: "anywhere" }}>
                {show(named.get(name), value, fields)}
              </Typography>
            </Box>
          ))}
          <Box>
            <Typography component="dt" variant="caption" color="text.secondary">Tags</Typography>
            <Stack component="dd" direction="row" sx={{ gap: 0.5, flexWrap: "wrap", m: 0 }}>
              {(record?.tags || []).length
                ? record.tags.map((t) => <Chip key={t} size="small" label={t} />)
                : <Typography variant="body2">None</Typography>}
            </Stack>
          </Box>
          <Box>
            <Typography component="dt" variant="caption" color="text.secondary">Where it came from</Typography>
            <Typography component="dd" variant="body2" sx={{ m: 0, overflowWrap: "anywhere" }}>
              {record?.source?.system} · {record?.source?.id}
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}

/**
 * The records behind a mark, read through the kernel and recorded as read.
 *
 * `request` is what the chart handed up: a spec to ask, or a reason it could
 * not be asked. Asking is `POST /insights/drill`, which reads each record the
 * way any read does and writes one audit row before returning any, so what is
 * on this screen is what the person may open now and has been logged.
 *
 * It says what the list is and is not, once: taken from a copy a few seconds
 * behind, then read from the kernel. It does not say how many were left out,
 * because a number there would describe a record this person may not open.
 */
export default function DrillDialog({ request, drill, schema, onClose }) {
  const [phase, setPhase] = useState({ name: "idle" });
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!request || request.refused) { setPhase({ name: "idle" }); return undefined; }
    let live = true;
    setPhase({ name: "loading" });
    drill(request.spec, request.from).then((out) => {
      if (!live) return;
      setPhase(out.ok ? { name: "done", result: out.result } : { name: "failed", error: out.error });
    });
    return () => { live = false; };
  }, [request, drill]);

  const result = phase.name === "done" ? phase.result : null;
  const columns = request?.type ? recordColumns(schema, request.type, 6, request.fields || []) : [];
  const fieldsByName = new Map(columns.map((c) => [c.name, c]));

  return (
    <>
      <Dialog open={Boolean(request)} onClose={onClose} maxWidth="lg" fullWidth scroll="paper" aria-labelledby="drill-title">
        <DialogTitle id="drill-title" sx={{ overflowWrap: "anywhere" }}>
          Records behind {request?.title || "this chart"}
        </DialogTitle>
        <DialogContent dividers>
          {request?.refused ? (
            <Alert severity="warning">{request.refused}</Alert>
          ) : phase.name === "loading" ? (
            <Loading label="Reading the records from the kernel" minHeight={120} />
          ) : phase.name === "failed" ? (
            isBadQuestion(phase.error) ? (
              <Alert severity="warning">
                <Typography variant="body2" sx={{ fontWeight: 600 }}>The kernel did not accept that question.</Typography>
                <Typography variant="body2">{phase.error.message}</Typography>
              </Alert>
            ) : (
              <Refusal failure={phase.error} actor="Insights" />
            )
          ) : result ? (
            <Stack sx={{ gap: 2 }}>
              {result.records.length === 0 ? (
                <EmptyState title="Nothing to show">
                  <Typography variant="body2">
                    No records you can open match this. That does not mean none exist.
                  </Typography>
                </EmptyState>
              ) : (
                <>
                  <Typography variant="body2" role="status">
                    {result.records.length.toLocaleString()} {result.records.length === 1 ? "record" : "records"}
                    {result.truncated
                      ? `, the first ${result.limit.toLocaleString()}. Add a filter to the chart to narrow it.`
                      : "."}
                  </Typography>
                  <DataTable
                    columns={[
                      ...columns.map((c) => ({
                        key: c.name,
                        label: humanise(c.name),
                        align: c.type === "integer" ? "right" : "left",
                        render: (row) => show(fieldsByName.get(c.name), row.fields?.[c.name], row.fields),
                      })),
                      {
                        key: "__tags", label: "Tags",
                        render: (row) => (row.tags || []).join(", ") || "none",
                      },
                      {
                        key: "__open", label: "Record",
                        render: (row) => (
                          <Button size="small" onClick={() => setDetail(row)} aria-label={`Open the record ${row.id}`}>
                            Open
                          </Button>
                        ),
                      },
                    ]}
                    rows={result.records}
                  />
                </>
              )}
              <Typography variant="caption" color="text.secondary">
                {result.basis}
                {result.as_of ? <> As of <time dateTime={result.as_of}>{new Date(result.as_of).toLocaleString()}</time>.</> : null}
              </Typography>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
      </Dialog>
      <RecordDetail record={detail} schema={schema} onClose={() => setDetail(null)} />
    </>
  );
}
